import ListIcon from '@eduzz/houston-icons/List';
import Button from '@eduzz/houston-ui/Button';
import Typography from '@eduzz/houston-ui/Typography';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Icon,
  IconButton,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import PermissionHide from 'components/PermissionHide';
import { Field, Form, Formik } from 'formik';
import moment from 'moment';
import React, { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';
import { tap } from 'rxjs/operators';
import { SelectFormField } from 'v2/components/Shared/FormFields/SelectFormField';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import { months, years } from 'v2/helpers/date';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import salesService from 'v2/services/sales/general';
import * as Yup from 'yup';

import { useStyles } from './styles';

import type { FormikProps } from 'formik';

const FormSchema = Yup.object().shape({
  email: Yup.string().email('Campo não é um e-mail válido'),
});

interface IReportForm {
  month: any;
  year: any;
  type: 'M' | 'S' | 'Y';
  half: any;
  email: string;
}

export interface IPeriodReportProps {}

const PeriodReport = () => {
  const [open, setOpen] = useState<boolean>(false);

  const handleToggle = () => {
    setOpen((prevOpen: boolean) => !prevOpen);
  };

  return (
    <div>
      <PermissionHide action='myeduzz:minhas-vendas:exportar'>
        <Button id='generate-report-button' variant='outlined' onClick={handleToggle} startIcon={<ListIcon />} fullWidth>
          Gerar relatório
        </Button>
        <ModalPeriodReport open={open} onClose={handleToggle} />
      </PermissionHide>
    </div>
  );
};
export default PeriodReport;

interface IModalPeriodReportProps {
  onClose: () => void;
  open: boolean;
}

const ModalPeriodReport = ({ open, onClose }: IModalPeriodReportProps) => {
  const classes = useStyles();
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [sentEmail, setSentEmail] = useState(null);
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const [onSubmit] = useCallbackObservable((values: IReportForm, form: FormikProps<IReportForm>) => {
    setLoading(true);
    return salesService.backgroundExport(values).pipe(
      tap(
        () => {
          setLoading(false);
          setSentEmail(values.email);
          form.setSubmitting(false);
          form.resetForm();
          setSuccess(true);
        },
        () => {
          setLoading(false);
          toast.error('Algo de errado aconteceu ao exportar, tente novamente');
          form.setSubmitting(false);
          setSuccess(false);
          setSentEmail(null);
        }
      )
    );
  }, []);

  const handleClose = useCallback(() => {
    onClose();
    setSuccess(false);
  }, [onClose, setSuccess]);

  return (
    <Formik
      initialValues={{ month: 1, year: moment().format('YYYY'), type: 'M', half: 1, email: '' }}
      validationSchema={FormSchema}
      onSubmit={onSubmit}
    >
      {({ isValid, isSubmitting, submitForm, values }) => (
        <Form>
          <Dialog maxWidth='sm' onClose={handleClose} aria-labelledby='advanced-filter' open={open}>
            <DialogTitle id='dialog-title'>
              <>
                Gerar relatório por período
                <IconButton size='small' className={classes.closeIcon} onClick={handleClose}>
                  <Icon color='primary'>close</Icon>
                </IconButton>
              </>
            </DialogTitle>
            <DialogContent dividers>
              {success ? (
                <Typography>
                  Geração do relatório solicitada com sucesso, você irá receber o relatório no e-mail <b>{sentEmail || user?.email}</b> quando o
                  processo finalizar.
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={12}>
                    <Field name='type' id='type'>
                      {({ field }: any) => (
                        <>
                          <FormLabel>Período</FormLabel>
                          <RadioGroup aria-label='Período' name='type' row {...field}>
                            <FormControlLabel value={'M'} control={<Radio />} label='Mensal' />
                            <FormControlLabel value={'S'} control={<Radio />} label='Semestral' />
                            <FormControlLabel value={'Y'} control={<Radio />} label='Anual' />
                          </RadioGroup>
                        </>
                      )}
                    </Field>
                  </Grid>
                  {values.type === 'S' && (
                    <Grid item xs={12} md={6}>
                      <Field
                        id='half'
                        name='half'
                        label='Semestre'
                        options={[
                          { label: 'Primeiro', value: 1 },
                          { label: 'Segundo', value: 2 },
                        ]}
                        component={SelectFormField}
                      />
                    </Grid>
                  )}
                  {values.type === 'M' && (
                    <Grid item xs={12} md={6}>
                      <Field id='month' name='month' label='Mês' options={months} component={SelectFormField} />
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <Field id='year' name='year' label='Ano' options={years} component={SelectFormField} />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <Field placeholder={user?.email} id='email' name='email' label='E-mail' options={years} component={TextFormField} />
                    <FormHelperText>
                      E-mail onde deseja receber o relatório, caso não informar, o mesmo será enviado em seu email de cadastro.
                    </FormHelperText>
                  </Grid>
                </Grid>
              )}
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
              <Button variant='text' onClick={handleClose}>
                Fechar
              </Button>
              {!success && (
                <Button variant='contained' loading={isSubmitting || loading} disabled={!isValid} type='submit' onClick={submitForm}>
                  Gerar
                </Button>
              )}
            </DialogActions>
          </Dialog>
        </Form>
      )}
    </Formik>
  );
};
